/**
 * Visual style for fillable user markups (rectangle, polygon, freeform, circle).
 */

export const HATCH_PATTERNS = {
  SOLID: 'solid',
  NONE: 'none',
  DIAGONAL: 'diagonal',
  CROSS: 'cross',
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  DOTS: 'dots',
};

export const HATCH_PATTERN_OPTIONS = [
  { id: HATCH_PATTERNS.SOLID, label: 'Solid fill' },
  { id: HATCH_PATTERNS.NONE, label: 'No fill' },
  { id: HATCH_PATTERNS.DIAGONAL, label: 'Diagonal' },
  { id: HATCH_PATTERNS.CROSS, label: 'Cross' },
  { id: HATCH_PATTERNS.HORIZONTAL, label: 'Horizontal' },
  { id: HATCH_PATTERNS.VERTICAL, label: 'Vertical' },
  { id: HATCH_PATTERNS.DOTS, label: 'Dots' },
];

export const DEFAULT_MARKUP_STYLE = {
  strokeColor: '#e97024',
  strokeWidth: 2,
  fillColor: '#e97024',
  fillOpacity: 0.15,
  hatchPattern: HATCH_PATTERNS.SOLID,
};

const SELECTED_STROKE = '#2563eb';
const VALID_HATCH = new Set(Object.values(HATCH_PATTERNS));

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex) {
  const raw = String(hex || '').replace('#', '');
  if (raw.length === 3) {
    return [
      parseInt(raw[0] + raw[0], 16),
      parseInt(raw[1] + raw[1], 16),
      parseInt(raw[2] + raw[2], 16),
    ];
  }
  if (raw.length >= 6) {
    return [
      parseInt(raw.slice(0, 2), 16),
      parseInt(raw.slice(2, 4), 16),
      parseInt(raw.slice(4, 6), 16),
    ];
  }
  return [233, 112, 36];
}

export function colorWithOpacity(hex, opacity) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(opacity, 0, 1)})`;
}

export function normalizeMarkupStyle(raw = {}) {
  const hatchPattern = VALID_HATCH.has(raw.hatchPattern)
    ? raw.hatchPattern
    : DEFAULT_MARKUP_STYLE.hatchPattern;
  return {
    strokeColor: typeof raw.strokeColor === 'string' && raw.strokeColor
      ? raw.strokeColor
      : DEFAULT_MARKUP_STYLE.strokeColor,
    strokeWidth: clamp(Number(raw.strokeWidth) || DEFAULT_MARKUP_STYLE.strokeWidth, 1, 6),
    fillColor: typeof raw.fillColor === 'string' && raw.fillColor
      ? raw.fillColor
      : DEFAULT_MARKUP_STYLE.fillColor,
    fillOpacity: clamp(Number(raw.fillOpacity ?? DEFAULT_MARKUP_STYLE.fillOpacity), 0, 1),
    hatchPattern,
  };
}

export function defaultStyleForFillableMarkup() {
  return { ...DEFAULT_MARKUP_STYLE };
}

export function isFillableMarkupGeometry(geometry, drawTool = '') {
  if (!geometry) return false;
  if (geometry.type === 'bbox') return true;
  if (geometry.type === 'circle') return true;
  if (geometry.type === 'polygon') {
    return Boolean(
      geometry.closed
      || drawTool === 'polygon'
      || drawTool === 'freeform',
    );
  }
  return false;
}

export function isFillableUserMarkup(component) {
  if (!component) return false;
  const isUser = component.source === 'user' || component.objectSource === 'user_drawn';
  if (!isUser) return false;
  return isFillableMarkupGeometry(component.geometry, component.drawTool);
}

export function resolveMarkupStyle(component, { selected = false } = {}) {
  const style = normalizeMarkupStyle(component?.style);
  return {
    ...style,
    strokeColor: selected ? SELECTED_STROKE : style.strokeColor,
  };
}

function createHatchTile(pattern, color) {
  const tile = document.createElement('canvas');
  const size = 12;
  tile.width = size;
  tile.height = size;
  const tctx = tile.getContext('2d');
  if (!tctx) return tile;

  tctx.strokeStyle = color;
  tctx.fillStyle = color;
  tctx.lineWidth = 1;

  switch (pattern) {
    case HATCH_PATTERNS.DIAGONAL:
      tctx.beginPath();
      tctx.moveTo(0, size);
      tctx.lineTo(size, 0);
      tctx.stroke();
      break;
    case HATCH_PATTERNS.HORIZONTAL:
      tctx.beginPath();
      tctx.moveTo(0, size / 2);
      tctx.lineTo(size, size / 2);
      tctx.stroke();
      break;
    case HATCH_PATTERNS.VERTICAL:
      tctx.beginPath();
      tctx.moveTo(size / 2, 0);
      tctx.lineTo(size / 2, size);
      tctx.stroke();
      break;
    case HATCH_PATTERNS.CROSS:
      tctx.beginPath();
      tctx.moveTo(0, size / 2);
      tctx.lineTo(size, size / 2);
      tctx.moveTo(size / 2, 0);
      tctx.lineTo(size / 2, size);
      tctx.stroke();
      break;
    case HATCH_PATTERNS.DOTS:
      tctx.beginPath();
      tctx.arc(size / 2, size / 2, 1.2, 0, Math.PI * 2);
      tctx.fill();
      break;
    default:
      break;
  }

  return tile;
}

function isHatchLinePattern(pattern) {
  return pattern !== HATCH_PATTERNS.SOLID && pattern !== HATCH_PATTERNS.NONE;
}

function paintSolidFill(ctx, style) {
  if (style.hatchPattern === HATCH_PATTERNS.NONE) return;
  if (style.fillOpacity <= 0) return;
  ctx.fillStyle = colorWithOpacity(style.fillColor, style.fillOpacity);
  ctx.fill();
}

function paintHatchFill(ctx, style) {
  if (!isHatchLinePattern(style.hatchPattern)) return;
  const hatchColor = colorWithOpacity(style.fillColor, Math.max(style.fillOpacity, 0.55));
  const tile = createHatchTile(style.hatchPattern, hatchColor);
  const pattern = ctx.createPattern(tile, 'repeat');
  if (!pattern) return;
  ctx.fillStyle = pattern;
  ctx.fill();
}

/** Clip to current path, paint fill + hatch, then restore. Path must be open on ctx. */
export function paintFillableStyle(ctx, style) {
  if (style.hatchPattern === HATCH_PATTERNS.NONE) return;

  ctx.save();
  ctx.clip();

  if (style.hatchPattern === HATCH_PATTERNS.SOLID) {
    paintSolidFill(ctx, style);
  } else {
    if (style.fillOpacity > 0) {
      ctx.globalAlpha = style.fillOpacity * 0.35;
      paintSolidFill(ctx, { ...style, hatchPattern: HATCH_PATTERNS.SOLID });
      ctx.globalAlpha = 1;
    }
    paintHatchFill(ctx, style);
  }

  ctx.restore();
}

export function strokeMarkupPath(ctx, style) {
  ctx.strokeStyle = style.strokeColor;
  ctx.lineWidth = style.strokeWidth;
  ctx.setLineDash([]);
  ctx.stroke();
}
